import styled from "@emotion/styled";
import type { FC } from "react";
import {
  IconCheckBold,
  IconCircleDashed,
  IconLockOutline,
  IconChevronRight
} from "src/constants/icons";
import type { RefillRequirement } from "src/state/RefillRequirementsCubit/RefillRequirementsTypes";
import { formatRequirementLabel } from "src/state/RefillRequirementsCubit/RefillRequirementsHelpers";
import { RefillColors } from "src/constants/refillColors";

const CHECK_GREEN = RefillColors.inRange;
const NEUTRAL_GRAY = RefillColors.gray;

/**
 * What the leading icon says about a row. Outstanding work is neutral rather than failed —
 * a red cross read as "you got this wrong" on requirements the member had no chance to do yet
 * (FEWEB-2666).
 */
type RowStatus = "done" | "locked" | "outstanding";

const STATUS_ICONS: Record<RowStatus, FC<{ size?: number }>> = {
  done: IconCheckBold,
  locked: IconLockOutline,
  outstanding: IconCircleDashed
};

// Base is a <button> (reset below) so an actionable row is keyboard-focusable/activatable;
// rendered as a plain <div> when not interactive. `& + &` shares one class across both tags,
// so the row dividers hold regardless.
//
// `as` must be filtered here for the swap to happen at all: emotion derives
// `shouldUseAs = !shouldForwardProp("as")`, so a custom predicate that lets `as` through makes
// emotion keep the base tag and forward `as` to the DOM instead.
const Row = styled("button", {
  shouldForwardProp: (p) => p !== "$interactive" && p !== "as"
})<{ $interactive: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem;
  min-height: 56px;
  width: 100%;
  margin: 0;
  border: none;
  background: none;
  font: inherit;
  color: inherit;
  text-align: left;
  cursor: ${({ $interactive }) => ($interactive ? "pointer" : "default")};

  & + & {
    border-top: 1px solid var(--color-cream-darker);
  }
`;

const Chevron = styled.div`
  display: flex;
  align-items: center;
  align-self: center;
  flex-shrink: 0;
`;

const StatusIcon = styled("div", {
  shouldForwardProp: (p) => p !== "$done"
})<{ $done: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  color: ${({ $done }) => ($done ? CHECK_GREEN : NEUTRAL_GRAY)};
`;

const Label = styled.span`
  flex: 1;
  min-width: 0;
  font-family: "STK Bureau", sans-serif;
  font-weight: 400;
  font-size: 1rem;
  line-height: 1.4;
  letter-spacing: -0.32px;
  color: var(--color-text);
`;

/**
 * Checklist row: status icon + label. Most requirements are completed via home-screen tasks,
 * so they're display-only. When an `onClick` is supplied (the actionable refill check-in) the
 * row becomes a button with a trailing chevron that opens the refill survey.
 *
 * A locked row is never interactive, whatever the caller passes: the backend rejects input on
 * it, so offering a tap target would lead straight into a refusal.
 */
const RefillRequirementRow: FC<{
  requirement: RefillRequirement;
  locked?: boolean;
  onClick?: () => void;
}> = ({ requirement, locked = false, onClick }) => {
  const interactive = Boolean(onClick) && !locked;
  const status: RowStatus = requirement.fulfilled
    ? "done"
    : locked
      ? "locked"
      : "outstanding";
  const Icon = STATUS_ICONS[status];

  return (
    <Row
      as={interactive ? undefined : "div"}
      type={interactive ? "button" : undefined}
      $interactive={interactive}
      onClick={interactive ? onClick : undefined}
      data-fulfilled={requirement.fulfilled}
      data-locked={locked}
    >
      <StatusIcon $done={requirement.fulfilled}>
        <Icon size={24} />
      </StatusIcon>
      <Label>{formatRequirementLabel(requirement)}</Label>
      {interactive && (
        <Chevron aria-hidden="true">
          <IconChevronRight />
        </Chevron>
      )}
    </Row>
  );
};

export default RefillRequirementRow;
